import React, { useState, useEffect } from 'react';
import { MediaItem, MediaFolder, MediaCollection } from '../../../../types/socialMediaPlanner';
import { SocialMediaPlannerService } from '../../../../services/socialMediaPlannerService';
import { MediaLibraryService } from '../../../../services/mediaLibraryService';

/**
 * Media Library Component
 * Manages media assets for social posts
 */
const MediaLibrary: React.FC = () => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaFolders, setMediaFolders] = useState<MediaFolder[]>([]);
  const [mediaCollections, setMediaCollections] = useState<MediaCollection[]>([]);
  const [currentFolder, setCurrentFolder] = useState<MediaFolder | null>(null);
  const [currentCollection, setCurrentCollection] = useState<MediaCollection | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'folders' | 'collections'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [altText, setAltText] = useState('');
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState<File | null>(null);
  const socialMediaService = new SocialMediaPlannerService();
  const mediaLibraryService = new MediaLibraryService();

  // Load media data
  useEffect(() => {
    const fetchMediaData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch media items based on current folder/collection
        let items: MediaItem[] = [];
        if (currentFolder && currentFolder.id) {
          items = await mediaLibraryService.getMediaByFolder(currentFolder.id);
        } else if (currentCollection && currentCollection.id) {
          items = await mediaLibraryService.getMediaByCollection(currentCollection.id);
        } else {
          items = await mediaLibraryService.getAllMedia();
        }
        setMediaItems(items);
        
        // Fetch folders and collections
        if (viewMode === 'folders' || viewMode === 'all') {
          const folders = await mediaLibraryService.getFolders();
          setMediaFolders(folders);
        }
        
        if (viewMode === 'collections' || viewMode === 'all') {
          const collections = await mediaLibraryService.getCollections();
          setMediaCollections(collections);
        }
      } catch (error) {
        console.error('Error fetching media data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMediaData();
  }, [currentFolder, currentCollection, viewMode]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadingFile(e.target.files[0]);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadingFile) return;

    try {
      setIsUploading(true);
      // Use mediaLibraryService instead of socialMediaService for media operations
      const result = await mediaLibraryService.uploadMedia(
        [uploadingFile], // API expects File[] as first parameter
        {
          tags: tags,
          altText: altText,
          caption: caption
        }
      );
      
      // Handle the result which could be MediaItem[] or MediaCollection
      const newItems = Array.isArray(result) ? result : [];
      
      setMediaItems([...newItems, ...mediaItems]);
      setUploadingFile(null);
      setAltText('');
      setCaption('');
      setTags([]);
      setVideoThumbnail(null);
    } catch (error) {
      console.error('Error uploading media:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Get filtered media items
  const getFilteredMedia = () => {
    if (!searchTerm) return mediaItems;
    return mediaItems.filter(
      item => item.file_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get icon for media type
  const getMediaTypeIcon = (type: string) => {
    switch (type) {
      case 'image': return (
        <div className="w-8 h-8 flex items-center justify-center text-blue-500">
          <div className="w-6 h-5 border-2 border-current rounded-sm relative">
            <div className="absolute bottom-1 right-1 w-2 h-2 bg-current rounded-full"></div>
          </div>
        </div>
      );
      case 'video': return (
        <div className="w-8 h-8 flex items-center justify-center text-purple-500">
          <div className="w-6 h-6 border-2 border-current rounded-full flex items-center justify-center">
            <div className="w-0 h-0 border-l-4 border-l-current border-t-transparent border-t-2 border-b-transparent border-b-2 ml-0.5"></div>
          </div>
        </div>
      );
      case 'gif': return (
        <div className="w-8 h-8 flex items-center justify-center text-green-500">
          <div className="w-6 h-5 border-2 border-current rounded-sm flex items-center justify-center">
            <span className="text-xs font-bold">GIF</span>
          </div>
        </div>
      );
      default: return (
        <div className="w-8 h-8 flex items-center justify-center text-gray-500">
          <div className="w-6 h-5 border-2 border-current rounded-sm">
            <div className="w-full h-0.5 bg-current mt-1 mx-auto" style={{width: '60%'}}></div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-white p-6 rounded shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Upload Media</h2>
        <div className="flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select File
            </label>
            <div className="flex items-center">
              <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-md p-3 hover:bg-gray-50 cursor-pointer">
                <input 
                  type="file" 
                  className="sr-only" 
                  onChange={handleFileChange}
                  accept="image/*,video/*"
                />
                <span className="flex items-center text-gray-500">
                  <div className="w-4 h-4 mr-2 flex items-center justify-center">
                    <div className="w-3 h-3 relative">
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-current"></div>
                      <div className="absolute h-2 w-0.5 bg-current left-1/2 transform -translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-1/4 right-1/4 border-l border-r border-b border-current h-1"></div>
                    </div>
                  </div>
                  {uploadingFile ? uploadingFile.name : 'Choose file'}
                </span>
              </label>
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alt Text (Accessibility)
            </label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe this image/video"
            />
          </div>
          <div>
            <button
              onClick={handleUpload}
              disabled={!uploadingFile || isUploading}
              className={`px-4 py-2 rounded-md ${
                !uploadingFile || isUploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      {/* Search & Library */}
      <div className="bg-white p-6 rounded shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Media Library</h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search media..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center">
              <div className="w-3 h-3 border-2 border-current rounded-full relative">
                <div className="absolute w-1.5 h-0.5 bg-current right-0 bottom-0 transform rotate-45 translate-x-1"></div>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {mediaItems.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto text-gray-300 w-16 h-16 mb-3 flex items-center justify-center">
                  <div className="w-12 h-10 border-2 border-current rounded-sm relative">
                    <div className="absolute bottom-1 right-1 w-3 h-3 bg-current rounded-full"></div>
                    <div className="absolute top-1 left-1 w-2 h-2 bg-current rounded-full"></div>
                  </div>
                </div>
                <p className="text-gray-500">Your media library is empty</p>
                <p className="text-sm text-gray-400 mt-1">Upload images and videos to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {getFilteredMedia().map((item) => (
                  <div key={item.id} className="group relative border rounded-md overflow-hidden">
                    <div className="aspect-w-1 aspect-h-1 bg-gray-100 flex items-center justify-center">
                      {item.file_type === 'image' ? (
                        <img 
                          src={item.file_url} 
                          alt={item.alt_text || item.file_name} 
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full p-2">
                          {getMediaTypeIcon(item.file_type)}
                          <span className="text-xs text-gray-500 mt-1 truncate w-full text-center">{item.file_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-white">
                      <p className="text-xs text-gray-600 truncate">{item.file_name}</p>
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-2 bg-white rounded-full hover:bg-red-100">
                        <div className="text-red-500 w-5 h-5 flex items-center justify-center">
                          <div className="w-4 h-4 relative">
                            <div className="absolute top-0 left-0.5 right-0.5 h-0.5 bg-current"></div>
                            <div className="absolute top-0.5 bottom-0 left-0 w-0.5 bg-current"></div>
                            <div className="absolute top-0.5 bottom-0 right-0 w-0.5 bg-current"></div>
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-current"></div>
                            <div className="absolute top-0.5 bottom-1 left-1.5 w-0.5 bg-current"></div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination placeholder */}
            {mediaItems.length > 0 && (
              <div className="flex justify-center mt-8">
                <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MediaLibrary;
